import { useSites, useSubscription } from '../api/hooks';
import { ErrorState, Loading } from '../components/States';
import { inr } from '../components/money';

/**
 * §2.4 — the pricing card is real, the checkout is not.
 *
 * There is no payment gateway: billingProvider is null in every organisation and
 * GET /api/org/subscription returns billing_portal_url: null with a comment
 * telling the client to treat that as "contact us". A dead Subscribe button is
 * worse than an honest one that says call us, and PRODUCT.md forbids inventing
 * pricing. So this screen quotes the real ladder and asks for a phone call.
 */

const PHONE = '+917667616304';
const PHONE_LABEL = '+91 76676 16304';

// Mirrors bhoomitrack-backend/utils/pricing.js. That file is the source of truth;
// if these ever disagree, it is right.
const TIERS = [
  { name: 'Site', sites: 1, monthly: 1500, annual: 12000 },
  { name: 'Contractor', sites: 4, monthly: 2000, annual: 20000 },
  { name: 'Builder', sites: 10, monthly: 5000, annual: 50000 },
];
const OVERAGE = { monthly: 500, annual: 5000 };

export default function Billing() {
  const { data, isPending, error, refetch } = useSubscription();
  const sites = useSites();

  if (isPending) return <Loading what="your plan" />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  const sub = data!;

  const count = sites.data?.length ?? 0;
  // Cheapest tier for the sites they actually run.
  const best = count > 0
    ? TIERS.map((t) => ({
        ...t,
        monthlyTotal: t.monthly + Math.max(0, count - t.sites) * OVERAGE.monthly,
        annualTotal: t.annual + Math.max(0, count - t.sites) * OVERAGE.annual,
      })).sort((a, b) => a.monthlyTotal - b.monthlyTotal)[0]
    : null;

  return (
    <>
      <div className="section__head">
        <h1 className="section__title">Plan &amp; billing</h1>
      </div>

      <div className="card" style={{ marginBottom: 'var(--s-xl)' }}>
        <div style={{ display: 'flex', gap: 'var(--s-xl)', flexWrap: 'wrap' }}>
          <Stat label="Plan" value={sub.plan} />
          <Stat label="Status" value={sub.status} tone={sub.active ? 'ok' : 'bad'} />
          <Stat label="Days remaining" value={sub.days_remaining === null ? '—' : `${sub.days_remaining}`}
            tone={sub.days_remaining !== null && sub.days_remaining <= 14 ? 'bad' : undefined} />
          <Stat label="Sites in use" value={String(count)} />
        </div>
        {!sub.active && sub.reason && <p className="warnbox warnbox--bad">{sub.reason}</p>}
      </div>

      <div className="section__head">
        <h2 className="section__title">What it costs</h2>
        <p className="section__sub muted">
          Priced per site. Annual is ten months for twelve. {count > 0 && best &&
            <>For your {count} site{count === 1 ? '' : 's'}, that is <strong>{inr(best.monthlyTotal)}/month</strong> or <strong>{inr(best.annualTotal)}/year</strong> on {best.name}.</>}
        </p>
      </div>

      <div className="tiles">
        {TIERS.map((t) => {
          const mine = best?.name === t.name;
          return (
            <div className={`card tile${mine ? ' tile--pick' : ''}`} key={t.name}>
              <div className="tile__label">{t.name}{mine && ' · your fit'}</div>
              <div className="tile__value mono-num">{inr(t.monthly)}<span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>/mo</span></div>
              <div className="tile__hint muted">
                Up to {t.sites} site{t.sites === 1 ? '' : 's'}, then {inr(OVERAGE.monthly)}/site
              </div>
              <div className="tile__action muted">{inr(t.annual)}/year</div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>To start or renew, call us</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          We take payment by UPI or bank transfer and activate your organisation the same day.
          There is no card form here yet — we would rather say that than show you a button that
          cannot take your money.
        </p>
        <a className="btn btn--gold" href={`tel:${PHONE}`}>Call {PHONE_LABEL}</a>
      </div>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'bad' }) {
  return (
    <div>
      <div className="tile__label">{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, textTransform: 'capitalize',
        color: tone === 'bad' ? 'var(--red)' : tone === 'ok' ? 'var(--green)' : undefined }}>{value}</div>
    </div>
  );
}
